import React, { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useLocation } from "react-router-dom";
import { isEmpty } from "lodash";

// Stripe stuff
import { loadStripe } from "@stripe/stripe-js";

import PurchaseTermsDisclosure from "./purchaseTermsDisclosure.jsx";
import {
  Elements,
  PaymentElement,
  PaymentRequestButtonElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";

import { withStyles } from "@mui/styles";
import MuiTextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";

import {
  Button,
  GridContainerWithSpacing as GridContainer,
  GridItemWithSpacing as GridItem,
} from "~/shared/components/MuiComponents.jsx";

import KickClient from "~/visits/utils/api.js";
import {
  getOrderComplete,
  getPromoCode,
  getVisitDifferentPharmacy,
  getVisitEmail,
  getVisitFullName,
  getVisitID,
  getVisitPhone,
  getVisitProduct,
} from "~/visits/utils/visitCookies.js";
import { useVisitIDRequired } from "~/visits/utils/visitUtils";
import { bugsnagClient } from "~/shared/utils/errorHandling";
import {
  isStripeElementNotReadyError,
  patientFacingStripeError,
  PAYMENT_GENERIC_ERROR_MESSAGE,
} from "~/shared/utils/stripeJsErrors";
import { isConsultationTypeInsomniaInitial } from "~/shared/utils/sharedUtils";
import { useOneTimePurchaseDisclosure } from "~/shared/experiments";

import { CheckoutProgress } from "../shared.jsx";
import {
  SocialProof,
  DoctorGuaranteeSection,
  WhatHappensNext,
  SleepConciergeInfo,
  SelectedSleepMeds,
} from "./paymentComponents.jsx";
import {
  checkoutPostData,
  completeCheckoutAndRedirect,
  getStripeOptions,
  paymentDetailsFormValidationSchema,
  orderTotalPriceWithCouponDiscount,
} from "./paymentHelpers.js";

import { computeDeviceFingerprint } from "~/visits/utils/deviceFingerprint";

import consultationStyles, {
  VISIT_MAX_WIDTH,
} from "~/visits/styles/consultationStyles.js";
import { kickNavy, kickAmaOrange } from "~/shared/styles/baseStyles.js";

let stripePromise;
const getStripe = () => {
  if (!stripePromise) {
    stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY, {
      betas: ["link_default_integration_beta_1"],
    });
  }
  return stripePromise;
};

// How long Confirm stays disabled waiting for the PaymentElement's `ready`
// event. Without a gate a patient on a slow connection could press a live
// Confirm above an empty card box; Stripe then rejected with IntegrationError
// "could not retrieve data from the specified Element" and the patient saw
// nothing happen. After the timeout the click is let through: either the
// iframe caught up and it works, or Stripe rejects and the retry message below
// is shown. The button is never permanently dead.
export const PAYMENT_ELEMENT_READY_TIMEOUT_MS = 15000;

export const PAYMENT_FORM_LOADING_MESSAGE =
  "The payment form is still loading. Please wait a moment and try again.";
export const PAYMENT_FORM_LOAD_FAILED_MESSAGE =
  "The payment form couldn't load. Check your connection and refresh the page to try again.";
export const PAYMENT_RETRY_MESSAGE =
  "We couldn't reach the payment form. Please wait a moment and press Confirm again, or refresh the page.";

// Hoisted out of PaymentDetails on purpose. A component defined inside another
// component is a brand-new type on every parent render, so every
// setMyCreditCardError / setFormState used to remount this form, tear down the
// PaymentElement iframe and create a second stripe.paymentRequest() (Bugsnag's
// "Frame not initialized." churn, and a lost card form after a declined card).
// It now mounts once per checkout.
export const BillingForm = ({
  classes,
  checkoutSessionID,
  consultationType,
  cardName,
  creditCardError,
  setupIntentClientSecret,
  history,
  buyWithInvoice,
  setFormState,
  setMyCreditCardError,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [paymentRequest, setPaymentRequest] = useState(null); // for apple pay

  // "loading" | "ready" | "timeout" | "failed" — see PAYMENT_ELEMENT_READY_TIMEOUT_MS.
  const [paymentElementState, setPaymentElementState] = useState("loading");
  // Messages about the form itself (not ready, load failed, retry). Kept as
  // form-local state so showing one never re-renders the parent.
  const [formMessage, setFormMessage] = useState("");

  // Only a subscription requires the terms tick, so the schema depends on the order
  // type. See paymentDetailsFormValidationSchema.
  const orderType = getVisitProduct(checkoutSessionID);

  // One-time orders are in an experiment on whether the disclosure panel renders at
  // all. Subscriptions are never eligible -- see useOneTimePurchaseDisclosure.
  const { ready: disclosureReady, hidden: disclosureHidden } =
    useOneTimePurchaseDisclosure(orderType, consultationType);
  // The PaymentRequest handlers below are registered once per mount; they read
  // the experiment's latest resolution through this ref rather than a stale
  // closure.
  const disclosureHiddenRef = useRef(disclosureHidden);
  disclosureHiddenRef.current = disclosureHidden;

  const validationSchema = useMemo(
    () => paymentDetailsFormValidationSchema(orderType),
    [orderType],
  );

  const {
    control,
    handleSubmit,
    getValues,
    trigger,
    formState: { isSubmitting, errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  useEffect(() => {
    if (paymentElementState !== "loading") {
      return undefined;
    }
    const timer = setTimeout(() => {
      setPaymentElementState((state) =>
        state === "loading" ? "timeout" : state,
      );
    }, PAYMENT_ELEMENT_READY_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [paymentElementState]);

  // pay using apple or google or msft pay
  const payUsingStripePaymentRequest = async (paymentMethod) => {
    const promoCode = getPromoCode(checkoutSessionID);
    const postData = checkoutPostData(
      getValues(),
      consultationType,
      checkoutSessionID,
      promoCode,
      { disclosureHidden: disclosureHiddenRef.current },
    );

    setFormState(getValues()); // save current form state to re-populate - call after grabbing react values

    await completeCheckoutAndRedirect({
      paymentMethodID: paymentMethod.id,
      postData,
      checkoutSessionID,
      consultationType,
      history,
      setError: setMyCreditCardError,
    });
  };

  useEffect(() => {
    // hook up Stripe PaymentRequest Button for Google / Apple Pay
    // only once per mount — a second PaymentRequest while a sheet is open
    // throws InvalidAccessError "Page already has an active payment session"
    if (stripe && !paymentRequest) {
      const pr = stripe.paymentRequest({
        country: "US",
        currency: "usd",
        total: {
          label: "",
          amount:
            parseInt(
              orderTotalPriceWithCouponDiscount(true, checkoutSessionID),
            ) * 100 || 0, // stripe expects value in cents
        },
        requestPayerName: true,
        requestPayerEmail: true,
      });

      // Check the availability of the Payment Request API.
      pr.canMakePayment()
        .then((result) => {
          if (result) {
            console.log("pr.canMakePayment result: ", result);
            setPaymentRequest(pr);

            pr.on("cancel", () => {
              // handle cancel event
            });

            pr.on("paymentmethod", async (event) => {
              const setupResult = await stripe.confirmCardSetup(
                setupIntentClientSecret.clientSecret,
                {
                  payment_method: event.paymentMethod.id,
                },
              );

              if (setupResult && setupResult.error) {
                // Close the wallet sheet as failed and tell the patient; a
                // developer-facing Stripe message never reaches them.
                setMyCreditCardError(
                  patientFacingStripeError(setupResult.error),
                );
                bugsnagClient.notify(
                  new Error(
                    `confirmCardSetup failed: ${setupResult.error.type}`,
                  ),
                  (bugsnagEvent) => {
                    bugsnagEvent.severity = "warning";
                    bugsnagEvent.unhandled = false;
                    bugsnagEvent.addMetadata("payment", {
                      checkoutSessionID,
                      stage: "confirmCardSetup",
                      type: setupResult.error.type,
                      code: setupResult.error.code,
                      declineCode: setupResult.error.decline_code,
                    });
                  },
                );
                event.complete("fail");
                return;
              }

              try {
                // completeCheckoutAndRedirect reports server-side failures
                // through setError and returns nothing, so the sheet is
                // completed as "success" (the payment method was accepted);
                // any checkout error is already on the page.
                await payUsingStripePaymentRequest(event.paymentMethod);
                event.complete("success");
              } catch (e) {
                console.error("Error [payUsingStripePaymentRequest]: ", e);
                bugsnagClient.notify(e, (bugsnagEvent) => {
                  bugsnagEvent.severity = "warning";
                  bugsnagEvent.unhandled = false;
                  bugsnagEvent.addMetadata("payment", {
                    checkoutSessionID,
                    stage: "payUsingStripePaymentRequest",
                  });
                });
                event.complete("fail");
              }
            });
          }
        })
        .catch((err) => {
          // Stripe rejects with "Frame not initialized." when the hidden
          // PaymentRequest iframe is torn down mid-flight (e.g. the component
          // unmounts during a checkout redirect). Apple/Google Pay is simply
          // unavailable in that case, so leave paymentRequest null — the wallet
          // button then doesn't render, identical to a falsy canMakePayment
          // result. Log only; do NOT notify Bugsnag, which would re-report this
          // benign, post-checkout noise as an unhandled error.
          console.warn("pr.canMakePayment() rejected: ", err);
        });
    }
  }, [stripe, paymentRequest]);

  const onSubmit = async (data) => {
    setFormMessage("");
    if (paymentElementState === "loading") {
      setFormMessage(PAYMENT_FORM_LOADING_MESSAGE);
      return;
    }
    try {
      await buyWithInvoice(data, stripe, elements, disclosureHiddenRef.current);
    } catch (e) {
      // Stripe.js rejects with IntegrationError when the PaymentElement iframe
      // is not mounted or not ready. It also rethrows the same error into
      // Bugsnag from its own message listener (downgraded to a handled warning
      // in stripeJsErrors.js), so no notify here; the metadata rides along on
      // that copy. Tell the patient instead of failing silently.
      console.error("buyWithInvoice error: ", e);
      bugsnagClient.addMetadata("payment", {
        checkoutSessionID,
        stage: "confirmSetup",
        paymentElementState,
      });
      setFormMessage(
        isStripeElementNotReadyError(e)
          ? PAYMENT_RETRY_MESSAGE
          : PAYMENT_GENERIC_ERROR_MESSAGE,
      );
    }
  };

  const confirmDisabled = isSubmitting || paymentElementState === "loading";

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      style={{ maxWidth: VISIT_MAX_WIDTH }}
    >
      <div>
        <div>
          <Controller
            render={({ field }) => (
              <MuiTextField
                {...field}
                label="Cardholder Name"
                variant="outlined"
                fullWidth
                autoFocus
              />
            )}
            name="cardName"
            control={control}
            defaultValue={cardName}
          />

          <p style={{ color: "red" }}>{errors.cardName?.message}</p>

          <div
            style={{
              border: "1px solid #C4C4C4",
              borderRadius: "4px",
              margin: "6px 0 0 0",
              padding: "20px 8px",
              minWidth: "350px", // requird for mobile
            }}
          >
            <PaymentElement
              options={{
                defaultValues: {
                  billingDetails: {
                    email: getVisitEmail(),
                    name: getVisitFullName(),
                    phone: getVisitPhone(),
                  },
                },
              }}
              onReady={() => {
                setPaymentElementState("ready");
                setFormMessage((message) =>
                  message === PAYMENT_FORM_LOADING_MESSAGE ? "" : message,
                );
              }}
              onLoadError={(loadError) => {
                setPaymentElementState("failed");
                setFormMessage(PAYMENT_FORM_LOAD_FAILED_MESSAGE);
                bugsnagClient.notify(
                  new Error("PaymentElement failed to load"),
                  (bugsnagEvent) => {
                    bugsnagEvent.severity = "warning";
                    bugsnagEvent.unhandled = false;
                    bugsnagEvent.addMetadata("payment", {
                      checkoutSessionID,
                      stage: "paymentElementLoad",
                      stripeError: loadError && loadError.error,
                    });
                  },
                );
              }}
            />
          </div>
          <p
            role="alert"
            style={{
              color: "red",
              marginTop: "15px",
              marginBottom: "10px",
              fontSize: "17px",
            }}
          >
            {creditCardError}
            {formMessage}

            {!isEmpty(errors) && "Please fill out all the fields above"}
          </p>
        </div>

        <DoctorGuaranteeSection classes={classes} />

        {/* Immediately above the pay button, before the charge — see the component.
            The checkbox is unticked by default and required by
            paymentDetailsFormValidationSchema, so the customer takes an affirmative
            action before any charge rather than being deemed to agree by paying. */}
        {disclosureReady && !disclosureHidden && (
          <Controller
            name="purchaseTermsAccepted"
            control={control}
            defaultValue={false}
            render={({ field }) => (
              <PurchaseTermsDisclosure
                orderType={orderType}
                checked={field.value}
                onChange={field.onChange}
                error={errors.purchaseTermsAccepted?.message}
              />
            )}
          />
        )}

        <Button
          type="submit"
          disabled={confirmDisabled}
          className={classes.confirmAndPayButton}
          style={{
            marginTop: "30px",
            fontSize: "18px",
            fontWeight: "bold",
            textTransform: "unset",
            backgroundColor: kickAmaOrange,
            color: kickNavy,
          }}
        >
          {paymentElementState === "loading"
            ? "Loading payment form…"
            : "Confirm"}
          {isSubmitting ? (
            <CircularProgress
              size="large"
              style={{
                position: "relative",
                left: "24px",
                top: "1px",
                color: kickNavy,
              }}
            />
          ) : null}
        </Button>

        {paymentRequest && !isSubmitting && (
          <div className={classes.stripePaymentRequestButtonBlock}>
            <p style={{ textAlign: "center", margin: "0 0 10px 0" }}>— or —</p>
            <PaymentRequestButtonElement
              options={{ paymentRequest }}
              onClick={async (event) => {
                // attempt to validate form before showing paymentRequest panel
                event.preventDefault();
                const result = await trigger();
                if (result) {
                  try {
                    paymentRequest.show();
                  } catch (e) {
                    // payment sheet already open (double-click) — ignore
                  }
                }
              }}
            />
          </div>
        )}
      </div>
    </form>
  );
};

const PaymentDetails = (props) => {
  const { classes } = props;
  const { consultationType } = props.match.params;
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const checkoutSessionID = queryParams.get("checkoutSessionID");

  useVisitIDRequired(checkoutSessionID);
  if (!checkoutSessionID || !getVisitID(checkoutSessionID)) {
    window.location.href = "/me/portal";
    return null;
  }

  const [creditCardError, setMyCreditCardError] = useState("");
  const [setupIntentClientSecret, setSetupIntentClientSecret] = useState("");
  const [{ cardName }, setFormState] = useState({
    cardName: getVisitFullName(),
  });

  // grab setup intent client secret from server
  useEffect(() => {
    // https://devtrium.com/posts/async-functions-useeffect
    if (getOrderComplete(checkoutSessionID)) {
      // if order is complete, don't let user go back to payment page
      props.history.push(
        `/visits/${consultationType}/payment/complete?checkoutSessionID=${checkoutSessionID}`,
      );
      window.location.href = `/visits/${consultationType}/payment/complete?checkoutSessionID=${checkoutSessionID}`;
    }
    console.log(
      "getVisitDifferentPharmacy: ",
      getVisitDifferentPharmacy(checkoutSessionID),
    );

    const fetchData = async () => {
      // get the client secret from server
      const client = KickClient.newAuthenticatedClient();
      const setupIntentResult = await client.get("/setup-intent");
      console.log("setupIntentResult.data: ", setupIntentResult.data);

      setSetupIntentClientSecret(setupIntentResult.data);
    };

    if (!setupIntentClientSecret) {
      fetchData().catch(console.error);
    }

    if (typeof window.Beacon == "function") {
      // window.Beacon("init", "e67f20b1-8927-4c18-b2a9-3e8119a361ad");
    }
  }, [checkoutSessionID, consultationType, setupIntentClientSecret]);

  // buyWithInvoice handles both subscription and non-subscription billing
  // disclosureHidden is resolved in BillingForm (where the panel renders) and passed
  // down, so the recorded terms version always matches what that customer was shown.
  const buyWithInvoice = async (values, stripe, elements, disclosureHidden) => {
    const promoCode = getPromoCode(checkoutSessionID);
    const postData = checkoutPostData(
      values,
      consultationType,
      checkoutSessionID,
      promoCode,
      { disclosureHidden },
    );

    // Nothing async may run between the Confirm click and stripe.confirmSetup.
    // Stripe opens the Apple Pay sheet from inside confirmSetup when the patient
    // picked Apple Pay in the PaymentElement, and Safari only allows that while the
    // click's user activation is still alive. Any macrotask boundary in between
    // (FingerprintJS yields to requestIdleCallback/setTimeout) ends it, and Stripe
    // rejects with "Failed to show the Apple Pay payment sheet". The device
    // fingerprint is computed further down, after Stripe has acted.
    const { setupIntent, error } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: "https://www.gokick.com/",
      },
      // Uncomment below if you only want redirect for redirect-based payments
      redirect: "if_required",
    });

    if (error) {
      // just a validation error - prevent page from refreshing
      if (error.type == "validation_error") {
        console.log("just return validation error");
        return;
      }

      setFormState(values); // save current form state to re-populate - call after grabbing react values
      setMyCreditCardError(patientFacingStripeError(error));
      if (error.type !== "card_error") {
        // A developer-facing failure (invalid_request_error, api_error, the Apple
        // Pay sheet not opening). Keep it observable as a handled warning now that
        // Stripe's own "Frame not initialized." noise is filtered out.
        bugsnagClient.notify(
          new Error(`confirmSetup failed: ${error.type}`),
          (bugsnagEvent) => {
            bugsnagEvent.severity = "warning";
            bugsnagEvent.unhandled = false;
            bugsnagEvent.addMetadata("payment", {
              checkoutSessionID,
              stage: "confirmSetup",
              type: error.type,
              code: error.code,
              declineCode: error.decline_code,
              message: error.message,
            });
          },
        );
      }
      return false;
    }
    const paymentMethodID = setupIntent.payment_method;

    // The computed device fingerprint for Visa Compelling Evidence 3.0. The other two
    // identifiers (kick_did, __stripe_mid) are cookies the server reads for itself.
    // All three are stored; which is submitted is decided when a dispute arrives.
    // Computed only now, after confirmSetup, for the user-activation reason above.
    if (postData) {
      postData.deviceFingerprint = await computeDeviceFingerprint();
    }

    setFormState(values); // save current form state to re-populate - call after grabbing react values

    await completeCheckoutAndRedirect({
      paymentMethodID,
      postData,
      checkoutSessionID,
      consultationType,
      history: props.history,
      setError: setMyCreditCardError,
    });
  };

  if (!setupIntentClientSecret) {
    return null;
  }

  return (
    <Elements
      stripe={getStripe()}
      options={getStripeOptions(setupIntentClientSecret)}
    >
      <div className={`${classes.containerWithSpacing} payment-section`}>
        <div className={classes.checkoutProgressMobile}>
          <CheckoutProgress step={2} checkoutSessionID={checkoutSessionID} />
        </div>
        <GridContainer>
          <GridItem xs={12} md={4}>
            <SocialProof
              style={{ borderBottom: "1px solid #ddd", marginBottom: "24px" }}
              showtestimonials={isConsultationTypeInsomniaInitial(
                consultationType,
              ).toString()}
            />
            {isConsultationTypeInsomniaInitial(consultationType) && (
              <>
                <WhatHappensNext />
                <SleepConciergeInfo />
                <SelectedSleepMeds />
              </>
            )}
          </GridItem>
          <GridItem xs={12} md={8}>
            <div className={classes.paymentForm}>
              <div className={classes.checkoutProgressDesktop}>
                <CheckoutProgress
                  step={2}
                  checkoutSessionID={checkoutSessionID}
                />
              </div>
              <BillingForm
                classes={classes}
                checkoutSessionID={checkoutSessionID}
                consultationType={consultationType}
                cardName={cardName}
                creditCardError={creditCardError}
                setupIntentClientSecret={setupIntentClientSecret}
                history={props.history}
                buyWithInvoice={buyWithInvoice}
                setFormState={setFormState}
                setMyCreditCardError={setMyCreditCardError}
              />
            </div>
          </GridItem>
        </GridContainer>
      </div>
    </Elements>
  );
};

export default withStyles(consultationStyles)(PaymentDetails);
